/**
 * React hooks for permission management
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { permissionsApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { useAuthStore } from '@/store/auth';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import type { GrantPermissionRequest, RevokePermissionRequest } from '@anchorbase/pillar-sdk';

const log = createComponentLogger('usePermissions');

/**
 * Fetch permissions for a role assignment
 */
export function useRoleAssignmentPermissions(roleAssignmentUuid: string | undefined) {
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    return useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'permissions',
            'roleAssignment',
            roleAssignmentUuid,
        ]),
        queryFn: async () => await permissionsApi.getRoleAssignmentPermissions(roleAssignmentUuid!),
        enabled: !!roleAssignmentUuid,
    });
}

/**
 * Grant a permission to a role assignment
 */
export function useGrantPermission() {
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    return useMutation({
        mutationFn: (data: GrantPermissionRequest) => permissionsApi.grantPermission(data),
        onSuccess: () => {
            // The read endpoint is still keyed by role-assignment ID.
            // Invalidate the shared prefix until that tasklet lands.
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'permissions',
                        'roleAssignment',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
        },
    });
}

/**
 * Revoke a permission from a role assignment
 */
export function useRevokePermission() {
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    return useMutation({
        mutationFn: (data: RevokePermissionRequest) => permissionsApi.revokePermission(data),
        onSuccess: () => {
            // The read endpoint is still keyed by role-assignment ID.
            // Invalidate the shared prefix until that tasklet lands.
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'permissions',
                        'roleAssignment',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
        },
    });
}
